import { Button, Checkbox, Form, Input, InputNumber, Select, Typography } from 'antd'
import { FormInstance } from 'antd/es/form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons'
import { useEffect, useRef } from 'react'

import { useMerchantApplication } from '../MerchantApplicationContext'
import { OwnerData } from '../merchantApplicationModel'
import { getForCountry } from '../../../services/statesService'
import EducationBlock from '../components/EducationBlock'
import StepFooter from '../components/StepFooter'

const { Title, Text } = Typography

type Props = {
  onNext: () => void
  onBack: () => void
}

const BusinessOwnersStep = ({ onNext, onBack }: Props) => {
  const { state, dispatch } = useMerchantApplication()
  const { owners } = state
  const formRefs = useRef<Record<string, FormInstance>>({})

  const canAddOwner = owners.length < 4

  const updateOwner = (ownerId: string, field: keyof OwnerData, value: string | number | boolean) => {
    dispatch({ type: 'UPDATE_OWNER', ownerId, field, value })
  }

  const handleNext = () => {
    const validations = owners.map(owner => {
      const form = formRefs.current[owner.id]
      return form?.validateFields() ?? Promise.resolve()
    })

    Promise.allSettled(validations).then(results => {
      const allValid = results.every(r => r.status === 'fulfilled')
      if (allValid) {
        onNext()
      }
    })
  }

  return (
    <>
      <div className="merchant-app-step-header">
        <Title level={3}>Business Owners</Title>
        <Text type="secondary">
          All owners with 25% or more ownership must be included. You can add up to 4 owners.
        </Text>
      </div>
      <div className="merchant-app-step-body">
        <EducationBlock title="Why do you need my Social Security Number?">
          <p style={{ margin: 0 }}>
            Payment processors are required by federal law (the Bank Secrecy Act) to verify the identity
            of business owners. Your SSN is used solely for identity verification — it is encrypted in
            transit and at rest, and is never stored by SynkedUP. It is transmitted directly to our
            payment processor, Rainforest, for verification only.
          </p>
        </EducationBlock>

        {owners.map((owner, index) => (
          <OwnerCard
            key={owner.id}
            owner={owner}
            index={index}
            onUpdate={updateOwner}
            onRemove={() => dispatch({ type: 'REMOVE_OWNER', ownerId: owner.id })}
            formRef={(form) => {
              if (form) {
                formRefs.current[owner.id] = form
              } else {
                delete formRefs.current[owner.id]
              }
            }}
          />
        ))}

        {canAddOwner && (
          <Button
            type="dashed"
            block
            size="large"
            onClick={() => dispatch({ type: 'ADD_OWNER' })}
            style={{ marginBottom: 16 }}
          >
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: 8 }} />
            Add Another Owner
          </Button>
        )}
      </div>
      <StepFooter onBack={onBack} onNext={handleNext} />
    </>
  )
}

type OwnerCardProps = {
  owner: OwnerData
  index: number
  onUpdate: (ownerId: string, field: keyof OwnerData, value: string | number | boolean) => void
  onRemove: () => void
  formRef: (form: FormInstance | null) => void
}

const OwnerCard = ({ owner, index, onUpdate, onRemove, formRef }: OwnerCardProps) => {
  const [form] = Form.useForm()
  const formRefCallback = useRef(formRef)
  formRefCallback.current = formRef

  useEffect(() => {
    formRefCallback.current(form)
    return () => formRefCallback.current(null)
  }, [form])

  useEffect(() => {
    form.setFieldsValue({
      firstName: owner.firstName,
      lastName: owner.lastName,
      email: owner.email,
      dateOfBirth: owner.dateOfBirth,
      ssn: owner.ssn,
      ownershipPercentage: owner.ownershipPercentage,
      ...(owner.useBusinessAddress ? {} : {
        address1: owner.address1,
        city: owner.city,
        stateCode: owner.stateCode,
        zip: owner.zip,
      }),
    })
  }, [form, owner.firstName, owner.lastName, owner.email, owner.dateOfBirth, owner.ssn,
      owner.ownershipPercentage, owner.useBusinessAddress, owner.address1, owner.city,
      owner.stateCode, owner.zip])

  return (
    <div className="merchant-app-owner-card">
      <div className="merchant-app-owner-card-header">
        <h4>Owner {index + 1}{index === 0 ? ' (You)' : ''}</h4>
        {index > 0 && (
          <Button
            type="text"
            danger
            size="small"
            onClick={onRemove}
          >
            <FontAwesomeIcon icon={faTrash} style={{ marginRight: 4 }} /> Remove
          </Button>
        )}
      </div>

      <Form form={form} layout="vertical" size="large">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <Form.Item label="First Name" name="firstName" rules={[{ required: true, message: 'Required' }]}>
            <Input
              value={owner.firstName}
              onChange={e => onUpdate(owner.id, 'firstName', e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Last Name" name="lastName" rules={[{ required: true, message: 'Required' }]}>
            <Input
              value={owner.lastName}
              onChange={e => onUpdate(owner.id, 'lastName', e.target.value)}
            />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email', message: 'Valid email required' }]}>
            <Input
              value={owner.email}
              onChange={e => onUpdate(owner.id, 'email', e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Phone">
            <Input
              value={owner.phone}
              onChange={e => onUpdate(owner.id, 'phone', e.target.value)}
            />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <Form.Item label="Date of Birth" name="dateOfBirth" rules={[{ required: true, message: 'Required' }]}>
            <Input
              placeholder="MM/DD/YYYY"
              value={owner.dateOfBirth}
              onChange={e => onUpdate(owner.id, 'dateOfBirth', e.target.value)}
              maxLength={10}
            />
          </Form.Item>
          <Form.Item label="Social Security Number" name="ssn" rules={[{ required: true, message: 'Required' }]}>
            <Input.Password
              placeholder="XXX-XX-XXXX"
              value={owner.ssn}
              onChange={e => onUpdate(owner.id, 'ssn', e.target.value)}
              maxLength={11}
            />
          </Form.Item>
        </div>

        <Form.Item
          label="Ownership Percentage"
          name="ownershipPercentage"
          rules={[{ required: true, message: 'Required' }, { type: 'number', min: 1, message: 'Must be at least 1%' }]}
        >
          <InputNumber
            style={{ width: 120 }}
            min={1}
            max={100}
            value={owner.ownershipPercentage}
            onChange={val => onUpdate(owner.id, 'ownershipPercentage', val ?? 0)}
            formatter={val => `${val}%`}
            parser={val => Number(val?.replace('%', '') ?? 0)}
          />
        </Form.Item>

        <Form.Item>
          <Checkbox
            checked={owner.useBusinessAddress}
            onChange={e => onUpdate(owner.id, 'useBusinessAddress', e.target.checked)}
          >
            Home address is the same as business address
          </Checkbox>
        </Form.Item>

        {!owner.useBusinessAddress && (
          <>
            <Form.Item label="Home Address" name="address1" rules={[{ required: true, message: 'Required' }]}>
              <Input
                value={owner.address1}
                onChange={e => onUpdate(owner.id, 'address1', e.target.value)}
              />
            </Form.Item>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0 16px' }}>
              <Form.Item label="City" name="city" rules={[{ required: true, message: 'Required' }]}>
                <Input
                  value={owner.city}
                  onChange={e => onUpdate(owner.id, 'city', e.target.value)}
                />
              </Form.Item>
              <Form.Item label="State" name="stateCode" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  value={owner.stateCode}
                  onChange={val => onUpdate(owner.id, 'stateCode', val)}
                  showSearch
                  optionFilterProp="label"
                  options={getForCountry('us').map(s => ({ value: s.code, label: s.code }))}
                />
              </Form.Item>
              <Form.Item label="ZIP" name="zip" rules={[{ required: true, message: 'Required' }]}>
                <Input
                  value={owner.zip}
                  onChange={e => onUpdate(owner.id, 'zip', e.target.value)}
                />
              </Form.Item>
            </div>
          </>
        )}
      </Form>
    </div>
  )
}

export default BusinessOwnersStep
